import React, { useState } from "react";
import axios from "axios";
import { Gift, Loader2, Eye, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import useReveal from "../lib/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MESSAGE_MAX = 120;

const initial = {
    voucher_type: "carte_blanche",
    amount: "",
    full_name: "",
    phone: "",
    email: "",
    message: "",
};

const VOUCHER_OPTIONS = [
    {
        value: "carte_blanche",
        title: "Carte Blanche",
        desc: "Offrez un repas sans prix défini. Vos proches profitent d'un délicieux repas dans notre restaurant ; nous vous enverrons directement l'addition une fois le bon utilisé.",
    },
    {
        value: "montant_defini",
        title: "Montant défini",
        desc: "Vos proches se régalent à hauteur du montant choisi. Pour toute dépense supplémentaire, elle leur sera directement facturée.",
    },
];

export const GiftVoucherPage = () => {
    useReveal();
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);
    const [previewing, setPreviewing] = useState(false);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const buildPayload = () => ({
        voucher_type: form.voucher_type,
        amount: form.voucher_type === "montant_defini" ? Number(form.amount) : null,
        full_name: form.full_name || "Aperçu",
        phone: form.phone || "0000000000",
        email: form.email || "apercu@example.com",
        message: form.message || null,
    });

    const onPreview = async () => {
        if (form.voucher_type === "montant_defini" && (!form.amount || Number(form.amount) <= 0)) {
            toast.error("Merci d'indiquer un montant pour générer l'aperçu.");
            return;
        }
        setPreviewing(true);
        try {
            const res = await axios.post(`${API}/gift-voucher/preview`, buildPayload(), {
                responseType: "blob",
            });
            const blob = new Blob([res.data], { type: "application/pdf" });
            const blobUrl = URL.createObjectURL(blob);
            // Utilise un lien avec target=_blank (contourne les popup blockers)
            const a = document.createElement("a");
            a.href = blobUrl;
            a.target = "_blank";
            a.rel = "noopener noreferrer";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        } catch (err) {
            // Si le serveur renvoie une erreur, blob contient du JSON
            let detail = "Impossible de générer l'aperçu. Merci de réessayer.";
            const data = err?.response?.data;
            if (data instanceof Blob) {
                try {
                    const text = await data.text();
                    const json = JSON.parse(text);
                    if (json?.detail) detail = json.detail;
                } catch (_) {}
            } else if (data?.detail) {
                detail = data.detail;
            }
            console.error("Preview error:", err);
            toast.error(detail);
        } finally {
            setPreviewing(false);
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.full_name || !form.email || !form.phone) {
            toast.error("Merci de renseigner votre nom, votre téléphone et votre e-mail.");
            return;
        }
        if (form.voucher_type === "montant_defini" && (!form.amount || Number(form.amount) <= 0)) {
            toast.error("Merci d'indiquer un montant pour le bon cadeau.");
            return;
        }

        setLoading(true);
        try {
            const payload = {
                voucher_type: form.voucher_type,
                amount: form.voucher_type === "montant_defini" ? Number(form.amount) : null,
                full_name: form.full_name,
                phone: form.phone,
                email: form.email,
                message: form.message || null,
            };
            await axios.post(`${API}/gift-voucher`, payload);
            toast.success("Merci ! Votre demande de bon cadeau a bien été envoyée. Nous vous recontactons rapidement.");
            setForm(initial);
        } catch (err) {
            const detail = err?.response?.data?.detail || "Une erreur est survenue. Merci de réessayer.";
            toast.error(detail);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="App font-sans bg-[#F9F8F6] text-[#2C1E16]">
            <Navbar />
            <main>
                <section
                    data-testid="bon-cadeau-section"
                    className="relative bg-[#F1EBE1] pt-36 sm:pt-44 pb-24 sm:pb-36 px-6 sm:px-12 lg:px-24"
                >
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center reveal mb-16">
                            <p className="text-[10px] sm:text-xs uppercase tracking-[0.4em] text-[#8B5A2B] font-bold mb-6">
                                — Bon Cadeau
                            </p>
                            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2C1E16] leading-[1.05] text-balance">
                                Un menu, un montant ou un{" "}
                                <span className="italic text-[#8B5A2B]">repas carte blanche</span> à offrir.
                            </h1>
                            <p className="mt-8 text-[#5C4033] text-base sm:text-lg leading-relaxed max-w-3xl mx-auto">
                                La Table d'Albigny vous propose deux formats de bon cadeau à offrir.
                                À vous de choisir celui qui fera plaisir.
                            </p>
                        </div>

                        <form
                            data-testid="gift-form"
                            onSubmit={onSubmit}
                            className="bg-white border border-brand-line p-8 sm:p-12 shadow-sm reveal"
                        >
                            {/* Choix du type */}
                            <fieldset className="mb-10">
                                <legend className="text-xs uppercase tracking-[0.25em] text-[#5C4033] font-semibold mb-5">
                                    Choisissez votre formule
                                </legend>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {VOUCHER_OPTIONS.map((opt) => {
                                        const active = form.voucher_type === opt.value;
                                        return (
                                            <label
                                                key={opt.value}
                                                data-testid={`voucher-option-${opt.value}`}
                                                className={`relative cursor-pointer border p-6 transition-all duration-300 ${
                                                    active
                                                        ? "border-[#5C4033] bg-[#F1EBE1] shadow-md"
                                                        : "border-brand-line hover:border-[#8B5A2B]"
                                                }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="voucher_type"
                                                    value={opt.value}
                                                    checked={active}
                                                    onChange={onChange}
                                                    className="sr-only"
                                                />
                                                <div className="flex items-start gap-4">
                                                    <Gift
                                                        size={26}
                                                        className={`shrink-0 mt-1 ${active ? "text-[#5C4033]" : "text-[#8B5A2B]"}`}
                                                        strokeWidth={1.5}
                                                    />
                                                    <div>
                                                        <p className="font-serif text-2xl text-[#2C1E16] mb-2">{opt.title}</p>
                                                        <p className="text-sm text-[#5C4033] leading-relaxed">{opt.desc}</p>
                                                    </div>
                                                </div>
                                                <span
                                                    aria-hidden="true"
                                                    className={`absolute top-4 right-4 h-4 w-4 rounded-full border-2 transition-all ${
                                                        active
                                                            ? "border-[#5C4033] bg-[#5C4033]"
                                                            : "border-brand-line bg-white"
                                                    }`}
                                                />
                                            </label>
                                        );
                                    })}
                                </div>
                            </fieldset>

                            {/* Champ montant (conditionnel) */}
                            {form.voucher_type === "montant_defini" && (
                                <div className="mb-8 space-y-2 max-w-sm">
                                    <Label htmlFor="amount" className="text-xs uppercase tracking-[0.2em] text-[#5C4033]">
                                        Montant du bon cadeau (€) *
                                    </Label>
                                    <Input
                                        id="amount"
                                        name="amount"
                                        type="number"
                                        min="1"
                                        step="1"
                                        placeholder="Ex : 80"
                                        value={form.amount}
                                        onChange={onChange}
                                        data-testid="gift-amount"
                                        required
                                        className="border-brand-line bg-[#F9F8F6] focus-visible:ring-[#5C4033]"
                                    />
                                </div>
                            )}

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div className="sm:col-span-2 space-y-2">
                                    <Label htmlFor="full_name" className="text-xs uppercase tracking-[0.2em] text-[#5C4033]">
                                        Votre Nom et Prénom *
                                    </Label>
                                    <Input
                                        id="full_name"
                                        name="full_name"
                                        value={form.full_name}
                                        onChange={onChange}
                                        data-testid="gift-fullname"
                                        required
                                        className="border-brand-line bg-[#F9F8F6] focus-visible:ring-[#5C4033]"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="phone" className="text-xs uppercase tracking-[0.2em] text-[#5C4033]">
                                        Votre Téléphone *
                                    </Label>
                                    <Input
                                        id="phone"
                                        name="phone"
                                        value={form.phone}
                                        onChange={onChange}
                                        data-testid="gift-phone"
                                        required
                                        className="border-brand-line bg-[#F9F8F6] focus-visible:ring-[#5C4033]"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-[#5C4033]">
                                        Votre Adresse Mail *
                                    </Label>
                                    <Input
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={form.email}
                                        onChange={onChange}
                                        data-testid="gift-email"
                                        required
                                        className="border-brand-line bg-[#F9F8F6] focus-visible:ring-[#5C4033]"
                                    />
                                </div>
                                <div className="sm:col-span-2 space-y-2">
                                    <Label htmlFor="message" className="text-xs uppercase tracking-[0.2em] text-[#5C4033]">
                                        Message à écrire sur le bon cadeau
                                    </Label>
                                    <Textarea
                                        id="message"
                                        name="message"
                                        rows={3}
                                        value={form.message}
                                        onChange={onChange}
                                        maxLength={MESSAGE_MAX}
                                        data-testid="gift-message"
                                        placeholder="Pour Marie, joyeux anniversaire…"
                                        className="border-brand-line bg-[#F9F8F6] focus-visible:ring-[#5C4033] resize-none"
                                    />
                                    <p
                                        data-testid="gift-message-counter"
                                        className={`text-xs text-right ${MESSAGE_MAX - form.message.length <= 10 ? "text-[#A33A2B] font-semibold" : "text-[#8B5A2B]"}`}
                                    >
                                        {MESSAGE_MAX - form.message.length} caractère(s) restant(s)
                                    </p>
                                </div>
                            </div>

                            <div className="mt-10 flex flex-col sm:flex-row gap-4">
                                <Button
                                    type="button"
                                    onClick={onPreview}
                                    disabled={previewing || loading}
                                    data-testid="gift-preview-btn"
                                    className="w-full sm:w-auto px-8 py-6 bg-transparent border border-[#5C4033] text-[#5C4033] hover:bg-[#5C4033] hover:text-[#F9F8F6] text-xs uppercase tracking-[0.3em] font-semibold rounded-none transition-all duration-300"
                                >
                                    {previewing ? (
                                        <>
                                            <Loader2 className="animate-spin mr-2" size={16} />
                                            Aperçu…
                                        </>
                                    ) : (
                                        <>
                                            <Eye className="mr-2" size={16} strokeWidth={1.8} />
                                            Aperçu du bon
                                        </>
                                    )}
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={loading || previewing}
                                    data-testid="gift-submit-btn"
                                    className="w-full sm:w-auto px-12 py-6 bg-[#5C4033] hover:bg-[#2C1E16] text-[#F9F8F6] text-xs uppercase tracking-[0.3em] font-semibold rounded-none transition-all duration-300"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="animate-spin mr-2" size={16} />
                                            Envoi en cours…
                                        </>
                                    ) : (
                                        "Envoyer ma demande"
                                    )}
                                </Button>
                            </div>
                        </form>
                        <div className="mt-10 text-center">
                            <Link
                                to="/backoffice"
                                data-testid="backoffice-link"
                                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] text-[#8B5A2B]/70 hover:text-[#5C4033] transition-colors"
                            >
                                <Lock size={11} strokeWidth={2} />
                                Backoffice
                            </Link>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};
